import { QueryClient } from '@tanstack/react-query';
import type {
  ApiResponse,
  ApiError,
  User,
  AuthResponse,
  LoginInput,
  OcrProcessResponse,
  OcrRequest,
  OcrResult,
  PaginatedResponse,
  ApiKeyListItem,
  ApiKeyWithSecret,
  CreateApiKeyInput,
  QuotaStatus,
  UsageEntry,
  Configuration,
  UpdateConfigurationInput,
  CreateUserInput,
  UpdateUserInput,
  SetUserQuotaInput,
  OcrProcessingStatus,
} from '@auctaim/shared';
import { ErrorCodes } from '@auctaim/shared';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/v1';

// Custom error class for API errors
export class ApiClientError extends Error {
  code: string;
  details?: Record<string, unknown>;
  status?: number;

  constructor(message: string, code: string, status?: number, details?: Record<string, unknown>) {
    super(message);
    this.name = 'ApiClientError';
    this.code = code;
    this.status = status;
    this.details = details;
  }
}

// Token refresh state management
let isRefreshing = false;
let refreshSubscribers: Array<(token?: string) => void> = [];

function subscribeTokenRefresh(callback: (token?: string) => void) {
  refreshSubscribers.push(callback);
}

function onTokenRefreshed(token?: string) {
  refreshSubscribers.forEach((callback) => callback(token));
  refreshSubscribers = [];
}

// QueryClient configuration with optimized defaults
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes - data is fresh for 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes - cache cleanup (formerly cacheTime)
      retry: (failureCount, error) => {
        // Don't retry on auth errors or client errors (4xx)
        if (error instanceof ApiClientError) {
          if (error.status && error.status >= 400 && error.status < 500) {
            return false;
          }
        }
        // Retry up to 2 times for other errors
        return failureCount < 2;
      },
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: true,
    },
    mutations: {
      retry: false, // Don't retry mutations by default
    },
  },
});

class ApiClient {
  private baseUrl: string;
  private refreshPromise: Promise<void> | null = null;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  /**
   * Handle token refresh logic
   * Uses a single refresh promise to prevent multiple concurrent refresh requests
   */
  private async handleTokenRefresh(): Promise<void> {
    if (isRefreshing && this.refreshPromise) {
      // Wait for ongoing refresh
      return this.refreshPromise;
    }

    isRefreshing = true;
    this.refreshPromise = this.refreshToken()
      .then(() => {
        onTokenRefreshed('refreshed');
      })
      .catch((error) => {
        onTokenRefreshed();
        throw error;
      })
      .finally(() => {
        isRefreshing = false;
        this.refreshPromise = null;
      });

    return this.refreshPromise;
  }

  /**
   * Refresh the access token using the refresh token stored in HTTP-only cookie
   */
  private async refreshToken(): Promise<void> {
    const url = `${this.baseUrl}/auth/refresh`;

    const response = await fetch(url, {
      method: 'POST',
      credentials: 'include', // Send cookies
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new ApiClientError('Token refresh failed', ErrorCodes.TOKEN_EXPIRED, response.status);
    }

    // New tokens are set in HTTP-only cookies by the server
    // No need to handle them in the client
  }

  /**
   * Core request method with automatic token refresh on 401
   */
  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    retryOn401 = true
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;

    const response = await fetch(url, {
      ...options,
      credentials: 'include', // Always include cookies for JWT auth
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    // Handle 401 Unauthorized - try to refresh token
    // Don't retry for /auth/me or /auth/refresh - these are auth check endpoints
    const isAuthCheckEndpoint = endpoint === '/auth/me' || endpoint === '/auth/refresh';
    if (response.status === 401 && retryOn401 && !isAuthCheckEndpoint) {
      try {
        // Attempt token refresh
        await this.handleTokenRefresh();

        // Retry the original request (only once)
        return this.request<T>(endpoint, options, false);
      } catch (refreshError) {
        // Refresh failed - clear auth state and redirect to login
        // This will be handled by the auth store
        throw new ApiClientError('Authentication failed', ErrorCodes.UNAUTHORIZED, 401);
      }
    }

    // Parse response
    const data: ApiResponse<T> = await response.json();

    // Handle error responses
    if (!response.ok || !data.success) {
      const errorMessage = data.error?.message || 'Request failed';
      const errorCode = data.error?.code || ErrorCodes.INTERNAL_ERROR;
      const errorDetails = data.error?.details;

      throw new ApiClientError(errorMessage, errorCode, response.status, errorDetails);
    }

    return data.data as T;
  }

  /**
   * GET request
   */
  async get<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET' });
  }

  /**
   * POST request
   */
  async post<T>(endpoint: string, body?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  /**
   * PUT request
   */
  async put<T>(endpoint: string, body?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  /**
   * PATCH request
   */
  async patch<T>(endpoint: string, body?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  /**
   * DELETE request
   */
  async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }

  /**
   * Upload file(s) using FormData
   */
  async upload<T>(endpoint: string, formData: FormData): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;

    const response = await fetch(url, {
      method: 'POST',
      credentials: 'include',
      body: formData,
      // Don't set Content-Type header - browser will set it with boundary
    });

    // Handle 401 with token refresh
    if (response.status === 401) {
      try {
        await this.handleTokenRefresh();

        // Retry upload
        const retryResponse = await fetch(url, {
          method: 'POST',
          credentials: 'include',
          body: formData,
        });

        const retryData: ApiResponse<T> = await retryResponse.json();

        if (!retryResponse.ok || !retryData.success) {
          throw new ApiClientError(
            retryData.error?.message || 'Upload failed',
            retryData.error?.code || ErrorCodes.INTERNAL_ERROR,
            retryResponse.status
          );
        }

        return retryData.data as T;
      } catch (error) {
        throw new ApiClientError('Authentication failed', ErrorCodes.UNAUTHORIZED, 401);
      }
    }

    const data: ApiResponse<T> = await response.json();

    if (!response.ok || !data.success) {
      throw new ApiClientError(
        data.error?.message || 'Upload failed',
        data.error?.code || ErrorCodes.INTERNAL_ERROR,
        response.status
      );
    }

    return data.data as T;
  }
}

// Export singleton API client instance
export const api = new ApiClient(API_BASE_URL);

// ============================================================================
// API Endpoint Helpers
// ============================================================================

/**
 * Authentication API endpoints
 */
export const authApi = {
  /**
   * Login with email and password
   * Returns user data and sets JWT tokens in HTTP-only cookies
   */
  login: (credentials: LoginInput) => api.post<AuthResponse>('/auth/login', credentials),

  /**
   * Refresh access token using refresh token from HTTP-only cookie
   */
  refresh: () => api.post<{ accessToken: string; expiresIn: number }>('/auth/refresh'),

  /**
   * Logout and clear all tokens
   */
  logout: () => api.post<void>('/auth/logout'),

  /**
   * Get current authenticated user
   */
  getCurrentUser: () => api.get<User>('/auth/me'),
};

/**
 * OCR processing API endpoints
 */
export const ocrApi = {
  /**
   * Process an image for OCR
   * @param formData - FormData containing the image file
   */
  process: (formData: FormData) => api.upload<OcrProcessResponse>('/ocr/process', formData),

  /**
   * Get paginated list of OCR requests
   */
  getRequests: (params?: { page?: number; limit?: number; status?: OcrProcessingStatus }) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', String(params.page));
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.status) searchParams.set('status', params.status);

    const query = searchParams.toString();
    return api.get<PaginatedResponse<OcrRequest>>(`/ocr/requests${query ? `?${query}` : ''}`);
  },

  /**
   * Get a specific OCR request by ID
   */
  getRequest: (id: string) => api.get<OcrRequest>(`/ocr/requests/${id}`),

  /**
   * Get OCR result by request ID
   */
  getResult: (id: string) => api.get<OcrResult>(`/ocr/results/${id}`),
};

/**
 * API Key management endpoints
 */
export const apiKeysApi = {
  /**
   * List all API keys for current user
   */
  list: () => api.get<{ apiKeys: ApiKeyListItem[] }>('/api-keys'),

  /**
   * Create a new API key
   * @param name - Name for the API key
   * @param description - Optional description
   * @returns API key with secret (only shown once)
   */
  create: (name: string, description?: string) =>
    api.post<{ apiKey: ApiKeyWithSecret }>('/api-keys', { name, description }),

  /**
   * Revoke an API key
   */
  revoke: (id: string) => api.delete<void>(`/api-keys/${id}`),
};

/**
 * Quota management endpoints
 */
export const quotaApi = {
  /**
   * Get current quota status for authenticated user
   */
  getStatus: () => api.get<QuotaStatus>('/quota'),

  /**
   * Get usage statistics
   * @param period - Time period for usage data
   * @returns Object with period and usage array
   */
  getUsage: (period?: 'daily' | 'weekly' | 'monthly') => {
    const query = period ? `?period=${period}` : '';
    return api.get<{ period: string; usage: UsageEntry[] }>(`/quota/usage${query}`);
  },
};

/**
 * System configuration endpoints (admin only)
 */
export const configApi = {
  /**
   * Get system configuration
   */
  get: () => api.get<Configuration>('/config'),

  /**
   * Update system configuration
   */
  update: (config: UpdateConfigurationInput) => api.put<Configuration>('/config', config),
};

/**
 * User management endpoints (admin only)
 */
export const usersApi = {
  /**
   * List users with optional filters
   */
  list: (params?: { page?: number; role?: string; search?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', String(params.page));
    if (params?.role) searchParams.set('role', params.role);
    if (params?.search) searchParams.set('search', params.search);

    const query = searchParams.toString();
    return api.get<PaginatedResponse<User>>(`/admin/users${query ? `?${query}` : ''}`);
  },

  /**
   * Create a new user
   */
  create: (data: CreateUserInput) => api.post<User>('/admin/users', data),

  /**
   * Update user details
   */
  update: (id: string, data: UpdateUserInput) => api.put<User>(`/admin/users/${id}`, data),

  /**
   * Delete a user
   */
  delete: (id: string) => api.delete<void>(`/admin/users/${id}`),

  /**
   * Set user quota
   * Backend expects { totalLimit: number, resetSchedule: 'monthly' | 'weekly' | 'daily' | 'never' }
   */
  setQuota: (
    id: string,
    input: { totalLimit: number; resetSchedule: 'monthly' | 'weekly' | 'daily' | 'never' }
  ) => api.put<QuotaStatus>(`/admin/users/${id}/quota`, input),
};

/**
 * PDPA (Personal Data Protection Act) endpoints
 */
export const pdpaApi = {
  /**
   * List all consents for current user
   */
  listConsents: () => api.get<any[]>('/pdpa/consent'),

  /**
   * Grant a consent
   */
  grantConsent: (consentType: string, version: string) =>
    api.post<any>('/pdpa/consent', { consentType, version }),

  /**
   * Withdraw a consent
   */
  withdrawConsent: (id: string) => api.put<void>(`/pdpa/consent/${id}/withdraw`),

  /**
   * Request data access (DSR - Data Subject Request)
   */
  requestAccess: () => api.post<any>('/pdpa/dsr/access'),

  /**
   * Request data export
   */
  requestExport: (format?: 'json' | 'csv') => api.post<any>('/pdpa/dsr/export', { format }),

  /**
   * Request account deletion
   */
  requestDeletion: (confirmation: string, reason?: string) =>
    api.post<any>('/pdpa/dsr/delete', { confirmation, reason }),

  /**
   * List all DSR requests for current user
   */
  listDsrRequests: () => api.get<any[]>('/pdpa/dsr/requests'),

  /**
   * Get audit trail of user's data access and modifications
   */
  getAuditTrail: (params?: { page?: number; actionType?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', String(params.page));
    if (params?.actionType) searchParams.set('actionType', params.actionType);

    const query = searchParams.toString();
    return api.get<PaginatedResponse<any>>(`/pdpa/audit/my-activity${query ? `?${query}` : ''}`);
  },
};

// ============================================================================
// Query Keys Factory
// ============================================================================

/**
 * Query key factory for consistent cache key management
 * This ensures type-safe and consistent query keys across the app
 */
export const queryKeys = {
  // Auth
  auth: {
    currentUser: () => ['auth', 'currentUser'] as const,
  },

  // OCR
  ocr: {
    all: () => ['ocr'] as const,
    requests: (params?: { page?: number; limit?: number; status?: OcrProcessingStatus }) =>
      ['ocr', 'requests', params] as const,
    request: (id: string) => ['ocr', 'request', id] as const,
    result: (id: string) => ['ocr', 'result', id] as const,
  },

  // API Keys
  apiKeys: {
    all: () => ['apiKeys'] as const,
    list: () => ['apiKeys', 'list'] as const,
  },

  // Quota
  quota: {
    status: () => ['quota', 'status'] as const,
    usage: (period?: 'daily' | 'weekly' | 'monthly') => ['quota', 'usage', period] as const,
  },

  // Config
  config: {
    current: () => ['config'] as const,
  },

  // Users (admin)
  users: {
    all: () => ['users'] as const,
    list: (params?: { page?: number; role?: string; search?: string }) =>
      ['users', 'list', params] as const,
  },

  // PDPA
  pdpa: {
    consents: () => ['pdpa', 'consents'] as const,
    dsrRequests: () => ['pdpa', 'dsrRequests'] as const,
    auditTrail: (params?: { page?: number; actionType?: string }) =>
      ['pdpa', 'auditTrail', params] as const,
  },
} as const;
